import { Skeleton, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  DigitalMonthlyReportBorrowingBase,
  DigitalMonthlyReportBorrowingBaseSection,
} from './DigitalMonthlyReportBorrowingBase';
import { DigitalMonthlyReportFacilitySummary } from './DigitalMonthlyReportFacilitySummary';
import { DigitalMonthlyReportFinancialCovenantOverview } from './DigitalMonthlyReportFinancialCovenantOverview';
import { DigitalMonthlyReportInputs } from './DigitalMonthlyReportInputs';
import { DigitalMonthlyReportMonthlyStatement } from './DigitalMonthlyReportMonthlyStatement';
import { DigitalMonthlyReportPortfolioCovenantOverview } from './DigitalMonthlyReportPortfolioCovenantOverview';
import {
  BorrowingBaseReportTab,
  DigitalMonthlyReportTab,
  isBorrowingBaseReportTab,
} from './DigitalMonthlyReportSidebar';
import { DigitalMonthlyReportSingleFinancialCovenantDetail } from './DigitalMonthlyReportSingleFinancialCovenantDetail';
import { DigitalMonthlyReportSinglePortfolioCovenantDetail } from './DigitalMonthlyReportSinglePortfolioCovenantDetail';
import { DigitalMonthlyReportTemplate } from './DigitalMonthlyReportTemplate';
import {
  ComplianceReportCfScopedGQLType,
  DeliverableContainerReceiptRecordGQLType,
  ReportingAccessorEntityAttributesGQLType,
} from '../../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useGetLastOutputDeliverableArtifactInstanceFromReceiptIdQuery } from '~/core/routes/deliverables/[deliverableId]/queries/GetLastOutputDeliverableArtifactInstanceFromReceiptId.gql.generated';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useGetCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const borrowingBaseReportTabToSection: Record<
  BorrowingBaseReportTab,
  DigitalMonthlyReportBorrowingBaseSection
> = {
  borrowingBase: 'all',
  borrowingBaseReceivables: 'receivables',
  borrowingBaseCash: 'cash',
  borrowingBaseSummary: 'finalBorrowingBaseReport',
};

const DigitalMonthlyReport = ({
  currentReceipt,
  lastOutputDeliverableArtifactInstanceFromReceiptId,
  creditAgreementId,
}: {
  currentReceipt: DeliverableContainerReceiptRecordGQLType;
  lastOutputDeliverableArtifactInstanceFromReceiptId: ComplianceReportCfScopedGQLType;
  creditAgreementId: string;
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const { creditFacilityId } = currentReceipt;

  const [selectedTab, setSelectedTab] = useState<DigitalMonthlyReportTab>(
    'facilitySummary',
  );

  const {
    loading: isCreditAgreementArtifactsLoading,
    data: creditAgreementArtifactsData,
  } = useGetCreditAgreementFullArtifactQuery({
    variables: {
      creditAgreementId,
    },
    fetchPolicy: 'network-only',
  });

  const creditAgreement: Util.Maybe<CreditAgreement.DomainEntity.CreditAgreementFullArtifact> = useMemo(() => {
    let parsedCreditAgreementFullArtifact = null;
    if (
      !creditAgreementArtifactsData ||
      !creditAgreementArtifactsData.creditAgreementFullArtifact
    ) {
      return null;
    }

    try {
      const {
        creditAgreement: creditAgreementRaw,
      } = creditAgreementArtifactsData.creditAgreementFullArtifact;
      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementRaw,
      );
      parsedCreditAgreementFullArtifact = parsedCreditAgreement;
    } catch (err) {
      const errorToastTitle = `Error parsing credit agreement artifacts for creditFacilityId ${creditFacilityId}`;
      toast({
        ...errorToastConfig,
        title: errorToastTitle,
        id: 'errorParsingCreditAgreement',
      });
      navigate('/');
    }

    return parsedCreditAgreementFullArtifact;
  }, [creditAgreementArtifactsData, navigate, creditFacilityId, toast]);

  const borrowingBaseReportId: string | null = useMemo(() => {
    if (!lastOutputDeliverableArtifactInstanceFromReceiptId) {
      return null;
    }
    const borrowingBaseReportEntityAttribute = _.find(
      lastOutputDeliverableArtifactInstanceFromReceiptId.entityRecords
        .accessorEntityAttributes,
      (entityAttribute) => entityAttribute.domain === 'borrowingBaseReport',
    );

    if (!borrowingBaseReportEntityAttribute) {
      return null;
    }

    return commonUtils.parseDbPgIdPointer(
      borrowingBaseReportEntityAttribute.identifier,
    );
  }, [lastOutputDeliverableArtifactInstanceFromReceiptId]);

  const portfolioCovenantAttributes:
    | ReportingAccessorEntityAttributesGQLType[]
    | null = useMemo(() => {
    if (!lastOutputDeliverableArtifactInstanceFromReceiptId) {
      return null;
    }
    const portfolioCovenants = _.filter(
      lastOutputDeliverableArtifactInstanceFromReceiptId.entityRecords
        .accessorEntityAttributes,
      (entityAttribute) => entityAttribute.domain === 'portfolioCovenant',
    );

    return portfolioCovenants;
  }, [lastOutputDeliverableArtifactInstanceFromReceiptId]);

  const portfolioCovenantKeys: string[] | null = useMemo(() => {
    if (!portfolioCovenantAttributes?.length) {
      return null;
    }

    const portCovKeys = _.chain(portfolioCovenantAttributes)
      .map((portCovAttribute) => portCovAttribute.supplementalEntityKey)
      .compact()
      .uniq()
      .value();

    return portCovKeys;
  }, [portfolioCovenantAttributes]);

  const financialCovenantAttributes:
    | ReportingAccessorEntityAttributesGQLType[]
    | null = useMemo(() => {
    if (!lastOutputDeliverableArtifactInstanceFromReceiptId) {
      return null;
    }
    const financialCovenants = lastOutputDeliverableArtifactInstanceFromReceiptId.entityRecords.accessorEntityAttributes.filter(
      (entityAttribute) => entityAttribute.domain === 'financialCovenant',
    );

    return financialCovenants;
  }, [lastOutputDeliverableArtifactInstanceFromReceiptId]);

  const financialCovenantKeys: string[] | null = useMemo(() => {
    if (!financialCovenantAttributes?.length) {
      return null;
    }

    const finCovKeys = _.chain(financialCovenantAttributes)
      .map((finCovAttribute) => finCovAttribute.supplementalEntityKey)
      .compact()
      .uniq()
      .value();

    return finCovKeys;
  }, [financialCovenantAttributes]);

  const monthlyStatementArtifactInstanceId = useMemo(() => {
    return currentReceipt.dependenciesInstances.find(
      (dependencyInstance) =>
        dependencyInstance.artifactType === 'monthlyStatement',
    )?.receiptInstanceId;
  }, [currentReceipt]);

  const hasMonthlyStatements: boolean = Boolean(
    monthlyStatementArtifactInstanceId,
  );

  if (
    !creditAgreementArtifactsData ||
    !creditAgreementArtifactsData.creditAgreementFullArtifact ||
    isCreditAgreementArtifactsLoading
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-digital-monthly-report-view-loading "
      />
    );
  }

  finleyAssert(
    creditAgreement,
    `Expect creditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );

  const reportingDate =
    lastOutputDeliverableArtifactInstanceFromReceiptId.reportingDate;

  return (
    <DigitalMonthlyReportTemplate
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
      hasMonthlyStatements={hasMonthlyStatements}
      financialCovenantKeys={financialCovenantKeys}
      portfolioCovenantKeys={portfolioCovenantKeys}
      borrowingBaseReportId={borrowingBaseReportId}
    >
      {selectedTab === 'facilitySummary' && (
        <DigitalMonthlyReportFacilitySummary
          creditAgreement={creditAgreement}
        />
      )}

      {/* Monthly Statements Group */}

      {selectedTab === 'monthlyStatements' && (
        <DigitalMonthlyReportMonthlyStatement
          monthlyStatementArtifactInstanceId={
            monthlyStatementArtifactInstanceId || ''
          }
        />
      )}

      {/* Borrowing Base Group */}
      {isBorrowingBaseReportTab(selectedTab) && !!borrowingBaseReportId && (
        <DigitalMonthlyReportBorrowingBase
          creditAgreement={creditAgreement}
          borrowingBaseReportId={borrowingBaseReportId}
          reportingDate={reportingDate}
          section={borrowingBaseReportTabToSection[selectedTab]}
        />
      )}

      {/* Portfolio Covenant Group */}
      {selectedTab === 'portfolioCovenant' &&
        !!portfolioCovenantAttributes?.length && (
          <DigitalMonthlyReportPortfolioCovenantOverview
            portfolioCovenantAttributes={portfolioCovenantAttributes}
          />
        )}
      {!!portfolioCovenantKeys &&
        portfolioCovenantKeys.map((covenantKey) => {
          if (selectedTab === covenantKey) {
            return (
              <DigitalMonthlyReportSinglePortfolioCovenantDetail
                creditFacilityId={creditFacilityId}
                creditAgreementPortfolioCovenants={
                  creditAgreement.portfolioCovenants
                }
                covenantKey={covenantKey}
              />
            );
          }

          return null;
        })}

      {/* Financial Covenant Group */}
      {selectedTab === 'financialCovenant' &&
        !!financialCovenantAttributes?.length && (
          <DigitalMonthlyReportFinancialCovenantOverview
            creditFacilityId={creditFacilityId}
            creditAgreementFinancialCovenants={
              creditAgreement.financialCovenants
            }
            financialCovenantAttributes={financialCovenantAttributes}
          />
        )}
      {!!financialCovenantKeys &&
        financialCovenantKeys.map((covenantKey) => {
          if (selectedTab === covenantKey) {
            return (
              <DigitalMonthlyReportSingleFinancialCovenantDetail
                creditFacilityId={creditFacilityId}
                creditAgreementFinancialCovenants={
                  creditAgreement.financialCovenants
                }
                covenantKey={covenantKey}
              />
            );
          }

          return null;
        })}

      {selectedTab === 'inputs' && (
        <DigitalMonthlyReportInputs currentReceipt={currentReceipt} />
      )}
    </DigitalMonthlyReportTemplate>
  );
};

export const DigitalMonthlyReportContainer = ({
  currentReceipt,
}: {
  currentReceipt: DeliverableContainerReceiptRecordGQLType;
}) => {
  const { id: currentReceiptId } = currentReceipt;

  const {
    data: deliverableArtifactInstanceData,
    loading: isDeliverableArtifactInstanceLoading,
  } = useGetLastOutputDeliverableArtifactInstanceFromReceiptIdQuery({
    fetchPolicy: 'no-cache',
    variables: {
      receiptId: currentReceiptId,
    },
  });

  if (
    !deliverableArtifactInstanceData ||
    isDeliverableArtifactInstanceLoading
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-digital-monthly-report-container-loading "
      />
    );
  }

  const {
    lastOutputDeliverableArtifactInstanceFromReceiptId,
  } = deliverableArtifactInstanceData;

  if (!lastOutputDeliverableArtifactInstanceFromReceiptId.creditAgreementId) {
    return null;
  }

  return (
    <DigitalMonthlyReport
      currentReceipt={currentReceipt}
      lastOutputDeliverableArtifactInstanceFromReceiptId={
        lastOutputDeliverableArtifactInstanceFromReceiptId
      }
      creditAgreementId={
        lastOutputDeliverableArtifactInstanceFromReceiptId.creditAgreementId
      }
    />
  );
};
